import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics } from 'analytics';
import { ownerSetOnboardingWalkthroughStep } from 'api/owner_sdk';
import WalkthroughIntroModal from 'components/Onboarding/WalkthroughIntroModal';
import { Walkthrough } from 'ui-kit';
import { WalkthroughFinishReason, WalkthroughStep } from 'ui-kit/Walkthrough';
import './StoreWalkthrough.scss';

export const STORE_WALKTHROUGH = 'store';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const QUICK_ADD_EXAMPLES = [
  'Mid-stay cleaning',
  'Pool Heating',
  'Ship a Package',
  'Grocery Delivery',
];

const STEPS: WalkthroughStep[] = [
  {
    id: 'store_details',
    target: '[data-walkthrough="store-edit"]',
    content: 'Edit your store name and details here.',
  },
  {
    id: 'product_toggle',
    target: '[data-walkthrough="product-toggle"]',
    title: 'Turn tiles on or off here.',
    content: <p>Guests won’t see any tiles that are inactive.</p>,
  },
  {
    id: 'product_menu',
    target: '[data-walkthrough="product-menu"]',
    title: 'Edit products with these three dots.',
    content: (
      <ul>
        <li>Set items to “Request & Approve.”</li>
        <li>Add contacts to receive sales notifications.</li>
        <li>Change price, photo, description, and more.</li>
      </ul>
    ),
  },
  {
    id: 'quick_add',
    target: '[data-walkthrough="quick-add"]',
    content: (
      <>
        <p>Add popular products in one click.</p>
        <ul className="StoreWalkthrough-examples">
          {QUICK_ADD_EXAMPLES.map((example) => (
            <li key={example}>{example}</li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'share_store',
    target: '[data-walkthrough="store-share"]',
    title: 'Share your store link in automated guest messages:',
    content: (
      <>
        <ul>
          <li>In booking confirmations</li>
          <li>One week before check-in</li>
          <li>With check-in notes</li>
        </ul>
        <p>
          <strong>Tip:</strong> Mention a few things guests can find in your store, like a spa
          treatment or private chef.
        </p>
        <p>
          <strong>This is how you start making sales.</strong>
        </p>
      </>
    ),
    ctaLabel: 'I’m ready!',
  },
];

interface LocationState {
  startWalkthrough?: string;
}

interface Props {
  storeId: number;
  onFinish?: () => void;
}

export default function StoreWalkthrough({ storeId, onFinish }: Props) {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const capture = useAnalytics();
  const open = location.state?.startWalkthrough === STORE_WALKTHROUGH;
  const [hasStarted, setStarted] = useState(false);
  const isIntroOpen = open && !hasStarted;

  const tourProperties = { store_id: storeId, total_steps: STEPS.length };

  const stepProperties = (step: WalkthroughStep) => ({
    ...tourProperties,
    step: step.id,
    step_index: STEPS.findIndex(({ id }) => id === step.id) + 1,
  });

  useEffect(() => {
    if (isIntroOpen) {
      capture('walkthrough_intro_shown', tourProperties);
    }
  }, [isIntroOpen]);

  // Re-arm the intro so a later store creation gets it again
  useEffect(() => {
    if (!open) {
      setStarted(false);
    }
  }, [open]);

  const handleStart = () => {
    capture('walkthrough_started', tourProperties);
    setStarted(true);
  };

  const handleStepView = (step: WalkthroughStep) => {
    capture('walkthrough_step_viewed', stepProperties(step));
    ownerSetOnboardingWalkthroughStep(step.id);
  };

  const handleFinish = (reason: WalkthroughFinishReason, step: WalkthroughStep) => {
    if (reason === 'completed') {
      capture('walkthrough_completed', stepProperties(step));
    } else if (reason === 'skipped') {
      capture('walkthrough_skipped', stepProperties(step));
    } else {
      capture('walkthrough_unavailable', stepProperties(step));
    }
    ownerSetOnboardingWalkthroughStep(step.id, true)
      .then(() => onFinish?.())
      .catch(() => message.error(GENERIC_ERROR));
    // The start signal lives in history state, which survives reloads and back/forward
    history.replace({ ...location, state: undefined });
  };

  return (
    <>
      <WalkthroughIntroModal open={isIntroOpen} onStart={handleStart} />
      <Walkthrough
        open={open && hasStarted}
        label="Store walkthrough"
        steps={STEPS}
        celebrate
        onStepView={handleStepView}
        onFinish={handleFinish}
      />
    </>
  );
}
